import { EventEmitter } from "node:events"
import { RealtimeEventRegistry } from "./event-registry"
import type { RealtimeTransport } from "./transport"
import {
	ConnectionStatus,
	type RealtimeConnectionConfig,
	type RealtimeEvent,
	type RealtimeEventHandler,
} from "./types"

export class RealtimeManager extends EventEmitter {
	private static instance: RealtimeManager | null = null

	private registry: RealtimeEventRegistry
	private transport: RealtimeTransport
	private status: ConnectionStatus = ConnectionStatus.DISCONNECTED
	private config: Required<RealtimeConnectionConfig>
	private reconnectAttempts = 0
	private reconnectTimer: NodeJS.Timeout | null = null
	private manualDisconnect = false
	private heartbeatTimer: NodeJS.Timeout | null = null

	private constructor(
		transport: RealtimeTransport,
		config: RealtimeConnectionConfig = {},
	) {
		super()
		this.transport = transport
		this.registry = new RealtimeEventRegistry()
		this.config = {
			autoConnect: false,
			reconnectInterval: 5000,
			maxReconnectAttempts: Number.MAX_SAFE_INTEGER, // Set to Infinity for unlimited retries
			heartbeatInterval: 30 * 1000,
			maxReconnectInterval: 60 * 1000, // 1 minute
			...config,
		}

		this.bindTransportEvents()

		if (this.config.autoConnect && typeof window !== "undefined") {
			this.connect()
			window.addEventListener("focus", this.handleWindowFocus)
		}
	}

	/**
	 * Get singleton instance
	 */
	static getInstance(
		transport: RealtimeTransport,
		config?: RealtimeConnectionConfig,
	): RealtimeManager {
		if (!RealtimeManager.instance) {
			RealtimeManager.instance = new RealtimeManager(transport, config)
		}
		return RealtimeManager.instance
	}

	/**
	 * Bind events from transport
	 */
	private bindTransportEvents(): void {
		this.transport.on("event", (event: RealtimeEvent) => {
			this.emit("event-received", event)
			this.registry.executeHandlers(event)
		})

		this.transport.on("statusChange", (status: ConnectionStatus) => {
			this.handleStatusChange(status)
		})

		this.transport.on("error", (error: Error) => {
			this.emit("error", error)
			this.handleStatusChange(ConnectionStatus.ERROR)
		})
	}

	private handleStatusChange(newStatus: ConnectionStatus): void {
		const oldStatus = this.status
		this.setStatus(newStatus)

		if (newStatus === ConnectionStatus.CONNECTED) {
			this.reconnectAttempts = 0
			this.clearReconnectTimer()
			this.manualDisconnect = false
			this.startHeartbeat()
		} else if (
			(oldStatus === ConnectionStatus.CONNECTED &&
				newStatus === ConnectionStatus.DISCONNECTED) ||
			newStatus === ConnectionStatus.ERROR
		) {
			this.stopHeartbeat()
			if (!this.manualDisconnect) {
				this.scheduleReconnect()
			}
		}
	}

	private handleWindowFocus = (): void => {
		if (
			this.status === ConnectionStatus.DISCONNECTED ||
			this.status === ConnectionStatus.ERROR
		) {
			this.forceReconnect()
		}
	}

	/**
	 * Connect to the transport
	 */
	async connect(): Promise<void> {
		if (
			this.status === ConnectionStatus.CONNECTED ||
			this.status === ConnectionStatus.CONNECTING
		) {
			return
		}
		this.manualDisconnect = false
		this.setStatus(ConnectionStatus.CONNECTING)
		await this.transport.connect()
	}

	/**
	 * Force reconnect (used after logout/login scenarios)
	 */
	async forceReconnect(): Promise<void> {
		this.manualDisconnect = false
		await this.disconnect()
		await this.connect()
	}

	/**
	 * Disconnect from the transport
	 */
	async disconnect(): Promise<void> {
		this.manualDisconnect = true
		this.clearReconnectTimer()
		this.stopHeartbeat()
		await this.transport.disconnect()
		this.setStatus(ConnectionStatus.DISCONNECTED)
	}

	/**
	 * Register event handler
	 */
	registerHandler<T = any>(
		handler: Omit<RealtimeEventHandler<T>, "id"> & { id?: string },
	): string {
		const id = this.registry.register(handler)
		this.emit("handler-registered", { ...handler, id } as RealtimeEventHandler)
		return id
	}

	/**
	 * Unregister event handler
	 */
	unregisterHandler(id: string): boolean {
		const success = this.registry.unregister(id)
		if (success) {
			this.emit("handler-unregistered", id)
		}
		return success
	}

	/**
	 * Unregister handlers by component
	 */
	unregisterHandlersByComponent(componentId: string): number {
		return this.registry.unregisterByComponent(componentId)
	}

	/**
	 * Get connection status
	 */
	getStatus(): ConnectionStatus {
		return this.status
	}

	/**
	 * Get handler count
	 */
	getHandlerCount(eventType?: string): number {
		return this.registry.getHandlerCount(eventType)
	}

	/**
	 * Enable or disable auto connect
	 */
	setAutoConnect(enabled: boolean): void {
		this.config.autoConnect = enabled
		if (!enabled) {
			this.clearReconnectTimer()
		}
	}

	/**
	 * Get auto connect status
	 */
	getAutoConnect(): boolean {
		return this.config.autoConnect
	}

	/**
	 * Set connection status and emit event
	 */
	private setStatus(status: ConnectionStatus): void {
		if (this.status !== status) {
			this.status = status
			this.emit("connection-status-changed", status)
		}
	}

	/**
	 * Schedule reconnection
	 */
	private scheduleReconnect(): void {
		if (
			!this.config.autoConnect ||
			this.reconnectTimer ||
			this.manualDisconnect
		) {
			return
		}

		if (this.reconnectAttempts >= this.config.maxReconnectAttempts) {
			this.setStatus(ConnectionStatus.ERROR)
			return
		}

		this.reconnectAttempts++
		const baseDelay =
			this.config.reconnectInterval * 2 ** (this.reconnectAttempts - 1)
		const reconnectDelay = Math.min(baseDelay, this.config.maxReconnectInterval)

		this.setStatus(ConnectionStatus.RECONNECTING)

		this.reconnectTimer = setTimeout(() => {
			this.reconnectTimer = null
			this.connect()
		}, reconnectDelay)
	}

	private clearReconnectTimer(): void {
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer)
			this.reconnectTimer = null
		}
	}

	private startHeartbeat(): void {
		this.stopHeartbeat() // Ensure no multiple heartbeats
		this.heartbeatTimer = setInterval(() => {
			if (this.status !== ConnectionStatus.CONNECTED) {
				return
			}

			const lastEventTimestamp = this.transport.getLastEventTimestamp()
			const now = Date.now()
			const heartbeatInterval = this.config.heartbeatInterval

			if (
				lastEventTimestamp &&
				now - lastEventTimestamp > heartbeatInterval + 5000 // Add 5s grace period
			) {
				this.forceReconnect()
			}
		}, this.config.heartbeatInterval)
	}

	private stopHeartbeat(): void {
		if (this.heartbeatTimer) {
			clearInterval(this.heartbeatTimer)
			this.heartbeatTimer = null
		}
	}

	/**
	 * Destroy the manager
	 */
	async destroy(): Promise<void> {
		if (typeof window !== "undefined") {
			window.removeEventListener("focus", this.handleWindowFocus)
		}
		await this.disconnect()
		this.removeAllListeners()
		this.registry.clear()
		RealtimeManager.instance = null
	}
}
